import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Dialog,
  Stack,
  Slide,
  Typography,
  Divider,
} from '@mui/material';
import CheckoutMap from './CheckoutMap';
import useEventContext from '@/contexts/event/useEventContext';
import EventSummary from './EventNav/EventSummary';
import EventInfo from './EventNav/EventInfo';
import HeaderMobile from './HeaderMobile';
import TicketsSelector from './tickets/TicketsSelector';
import TicketSummary from './tickets/TicketSummary';
import { styled } from '@mui/material/styles';
import { useTicketsSelected, useFan2Fan } from '@/store';
import BoletiaMobileFooter from '@/components/BoletiaMobileFooter';
import TicketCard from './ticket/TicketCard';
import Fan2FanRedirectDialog from '@/components/dialogs/Fan2FanRedirectDialog';
import FooterSummary from './FooterSummary';
import Iconify from '@/components/Iconify';
import BookingSummary from './EventNav/BookingSummary';
import BackButton from '../components/BackButton';
import PromotionButton from '../../../sections/promotion/PromotionButton';
import ArrowIcon from '@/components/ArrowIcon';
import RenderIf from '@/components/RenderIf';
import Visibility from '@/components/Visibility';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useEventStatus from '@/contexts/event/useEventStatus';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import EventEmpty from '@/assets/illustration_EventEmpty';
import MsiPreSale from '@/sections/msi/MsiPreSale';
import MsciBanner from '@/sections/msi/MsciBanner';
import Fan2FanTickets from '@/sections/secondary-market/Fan2FanTickets';
import CustomLinkBanner from '@/sections/custom-links/CustomLinkBanner';
import EventAlert from './EventAlert';
import useEventAlerts from '@/hooks/checkout/useEventAlerts';
import { useCustomLinksActions, useEmbedded } from '@/store';
import RecaptchaLabel from '@/components/RecaptchaLabel';
import useMobile from '@/hooks/useMobile';
import usePostMessages from '@/hooks/messages/usePostMessages';

const HOME_OPEN = 10;
const HOME_CLOSE = 50;
const HOME_CLOSE_EMPTY = 70;

const BoxStyle = styled(Box)(({ theme }) => ({
  transition: theme.transitions.create(['height', 'top'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return (
    <Box
      sx={{
        'width': '32px',
        'height': '32px',
        '& .MuiSvgIcon-root': {
          fill: 'none',
          fontSize: '32px',
          width: '32px',
          height: '32px',
        },
      }}
      {...other}
    >
      <ArrowIcon />
    </Box>
  );
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function EventCartDesktop() {
  const { t } = useTranslation();
  const [view, setView] = useState('home');
  const [show, setShow] = useState(false);
  const [sizeClose, setSizeClose] = useState(HOME_CLOSE);
  const { hasTickets } = useTicketsSelected();
  const { showEventDetails } = useAnalyticsEvents();
  const { eventStatus } = useEventStatus();
  const { event } = useEventContext();
  const { alert } = useEventAlerts(eventStatus);
  const {
    customLink: { linkStatus },
  } = useCustomLinksActions();

  const { callBackAction } = useTicketsContext();
  const { isMobile } = useMobile();
  const { isEmbedded, backButtonMessage, onReceiveMessage } = usePostMessages();

  const { link, open: openF2FDialog, setOpen: setOpenF2FDialog, setShowFan2Fan } = useFan2Fan();

  const [openEventInfo, setOpenEventInfo] = useState(false);

  const scrollRef = useRef(null);

  const toggleOpenEventInfo = () => {
    setOpenEventInfo((prev) => {
      if (!prev) {
        showEventDetails();
      }
      backButtonMessage(!prev);
      return !prev;
    });
  };

  const [open, setOpen] = useState(false);
  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const {
    event: {
      event: { use_seats, about },
    },
  } = useEventContext();

  const onTicketCategorySelected = () => {
    // toggleDrawer();
    setView('summary');
  };

  const onBack = () => {
    setView('home');
  };

  const getDrawPosition = (useSeats = false) => {
    if (view === 'summary') {
      return {
        top: open ? `${HOME_OPEN}%` : `${sizeClose}%`,
        height: open ? `${100 - HOME_OPEN}%` : `${100 - sizeClose}%`,
      };
    }
    return {
      top: open ? `${HOME_OPEN}%` : `${sizeClose}%`,
      height: open ? `${100 - HOME_OPEN}%` : `${100 - sizeClose}%`,
    };
  };

  useEffect(() => {
    setShow(true);
  }, []);

  useEffect(() => {
    if (
      eventStatus === 'inactive' ||
      eventStatus === 'soldout' ||
      eventStatus === 'not-on-sale' ||
      eventStatus === 'expired' ||
      eventStatus === 'link-expired' ||
      eventStatus === 'pre-sale'
    ) {
      setSizeClose(HOME_CLOSE_EMPTY);
    }
  }, [eventStatus]);

  useEffect(() => {
    backButtonMessage(view === 'summary');
  }, [view]);

  useEffect(() => {
    const backMessage = onReceiveMessage((message) => {
      if (message !== 'back') return;

      if (openEventInfo) {
        toggleOpenEventInfo();
        return;
      }

      if (view === 'summary') {
        callBackAction(onBack);
        return;
      }
    });

    return backMessage;
  }, [openEventInfo, view]);

  useEffect(() => {
    const handleScrollToBottom = (e) => {
      if (isMobile && scrollRef.current) {
        scrollRef.current.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
    };

    window.addEventListener('promotion-modal-closed', handleScrollToBottom);
    return () => {
      window.removeEventListener(
        'promotion-modal-closed',
        handleScrollToBottom,
      );
    };
  }, [isMobile]);

  const renderHeader = (path) => {
    if (!isMobile || !isEmbedded) return view === path;

    if (!use_seats) return false;

    return path === 'home' && view === 'home';
  };

  return (
    <Box ref={scrollRef} sx={{ height: '100%', overflowY: 'auto' }}>
      <RenderIf condition={renderHeader('home')}>
        <HeaderMobile onInfo={toggleOpenEventInfo} />
      </RenderIf>
      <RenderIf condition={renderHeader('summary')}>
        <HeaderMobile onInfo={toggleOpenEventInfo}>
          <Container>
            <Stack direction="row" spacing={0} sx={{ alignItems: 'center' }}>
              <BackButton
                onClick={onBack}
                sx={{
                  position: 'unset',
                }}
              />
            </Stack>
          </Container>
        </HeaderMobile>
      </RenderIf>
      <Dialog fullScreen open={openEventInfo} TransitionComponent={Transition}>
        <Container>
          <Visibility visibleIn="web">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '56px',
              }}
            >
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {t('checkout.global.aboutEvent')}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Box
                sx={{
                  'width': '20px',
                  'height': '20px',
                  'display': 'flex',
                  'alignItems': 'center',
                  'justifyContent': 'center',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={toggleOpenEventInfo}
              >
                <Iconify
                  width={16}
                  height={16}
                  icon={'material-symbols:close'}
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[200],
                  }}
                />
              </Box>
            </Box>
          </Visibility>
        </Container>
        <Divider sx={{ mb: 2 }} />
        <Container>
          <EventInfo />
        </Container>
      </Dialog>
      <Box
        sx={{
          pt: renderHeader(view) ? 8 : 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          background: (theme) => {
            if (theme.palette.mode === 'light') {
              return eventStatus !== 'active' && use_seats ? '#FAFAFF' : '#FFF';
            }
            return eventStatus !== 'active' && use_seats
              ? '#FAFAFF'
              : theme.palette.grey[900];
          },
        }}
      >
        <RenderIf condition={show}>
          <RenderIf condition={use_seats}>
            <RenderIf
              condition={
                (linkStatus === 'active' || !linkStatus) &&
                (eventStatus === 'active' || eventStatus === 'pre-sale')
              }
            >
              {' '}
              {view === 'home' && <CustomLinkBanner />}

              <Box sx={{ height: 'calc(50% - 35px)' }}>
                <CheckoutMap
                  onHold={onTicketCategorySelected}
                  sx={{
                    height: 'calc(100% - 45px)',
                  }}
                  view={view}
                />
              </Box>
            </RenderIf>
            <RenderIf
              condition={
                eventStatus === 'inactive' ||
                eventStatus === 'soldout' ||
                eventStatus === 'not-on-sale' ||
                eventStatus === 'expired' ||
                eventStatus === 'link-expired'
              }
            >
              <EventEmpty
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  mt: '50px',
                }}
              />
            </RenderIf>
          </RenderIf>
          <RenderIf condition={!use_seats}>
            <Container
              sx={{
                height: `calc(${sizeClose}% - 38px)`,
                overflow: 'scroll',
              }}
            >
              <EventSummary onReadMore={toggleOpenEventInfo} />
            </Container>
          </RenderIf>
        </RenderIf>
        <BoxStyle
          sx={{
            ...getDrawPosition(use_seats),
            position: 'absolute',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderTop: (theme) =>
              theme.palette.mode === 'light'
                ? 0
                : `1px solid ${theme.palette.grey[600]}`,
            visibility: 'visible',
            right: 0,
            left: 0,
            background: (theme) => theme.palette.background.default,
            width: '100%',
            overflow: 'scroll',
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {(eventStatus === 'active' || eventStatus === 'pre-sale') && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={toggleDrawer}
            >
              <ExpandMore expand={open} />
            </Box>
          )}
          <Container sx={{ pt: 0 }}>
            {show && (
              <>
                <Slide
                  direction="left"
                  in={view === 'summary'}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <BookingSummary changeView={setView} />
                    {hasTickets && (
                      <Box
                        sx={{
                          height: '77px',
                          width: '100%',
                        }}
                      ></Box>
                    )}
                  </Box>
                </Slide>
                {view === 'home' && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      pb: 2
                    }}
                  >
                    {/* Banner Fan2Fan */}

                    {use_seats && event?.secondary_market?.has && (
                      <TicketCard
                        variant="fan2fan"
                        standAlone
                        styles={{ display: 'flex', justifyContent: 'center', py: '9px' }}
                        onClick={() => setShowFan2Fan({ open: true, link: event.secondary_market.link })}
                      >
                        <Typography sx={{ fontSize: '14px', display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          Boletos de Fan a Fan disponibles
                          <Iconify icon="ph:caret-right-bold" />
                        </Typography>
                      </TicketCard>
                    )}
                    <Box sx={{ mt: 2 }}>
                      <MsciBanner />
                    </Box>
                    {eventStatus === 'pre-sale' && <MsiPreSale />}
                    {(linkStatus === 'active' || !linkStatus) &&
                      eventStatus === 'active' && (
                        <TicketsSelector
                          onTicketCategorySelected={onTicketCategorySelected}
                        />
                      )}
                    {event?.secondary_market?.has && (
                      <Fan2FanTickets
                        link={event.secondary_market.link}
                        setShowFan2Fan={setShowFan2Fan}
                      />
                    )}
                    {alert && (
                      <EventAlert
                        title={t(alert.title)}
                        message={t(alert.message)}
                        hasLink={alert.link}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </Container>
        </BoxStyle>
      </Box>
      <RecaptchaLabel />
      <Fan2FanRedirectDialog
        open={openF2FDialog}
        title="¿Quieres continuar a Fan a Fan?"
        text="Serás redirigido a Fan a Fan, nuestro espacio donde puedes adquirir boletos de otros asistentes de forma segura."
        acceptLabel="Continuar a Fan a Fan"
        backLabel="Quedarme en Boletia"
        onConfirm={() => window.open(link, '_blank')}
        onCancel={() => setOpenF2FDialog(false)}
      />
      <BoletiaMobileFooter hasTickets={hasTickets} />
      {show && hasTickets && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: (theme) => theme.palette.background.default,
            boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.16)',
            zIndex: 1000,
          }}
        >
          <Container>
            <FooterSummary />
          </Container>
        </Box>
      )}
    </Box>
  );
}
